/* Abandoned-cart signal.

   Tells our Apps Script backend that a KNOWN shopper has a live cart, so it can
   sign and forward that to Fire AI, who run the WhatsApp AC1 message. We never
   touch a phone number or an email: the only identifier we send is `zc_chat_id`,
   the opaque id Fire AI's own /submit-profile API issued when the shopper
   finished the AI Bat Fit test (see assets/zac-api.js). They resolve it back to
   a person on their side.

   WHY THIS EXISTS AT ALL, rather than a Shopify webhook:
   - There is no cart-abandonment webhook. carts/create and carts/update are
     anonymous — no phone, no email — so nothing to match a session against.
   - checkouts/create fires before the shopper types anything (customer and
     email arrive null), and this store runs Razorpay Magic Checkout, which
     bypasses Shopify's checkout entirely. Neither can see this drop-off.
   The storefront is the ONLY place that knows both who the shopper is and what
   is in their cart. Hence here.

   No chat_id means no fit test means nobody to message — we do nothing at all,
   and cost the page one cookie read. */
(function () {
  'use strict';

  var cfg = window.SC_CART_SIGNAL;
  if (!cfg || !cfg.endpoint || !cfg.pageToken) return;

  var CHAT_ID_KEY = 'zc_chat_id';   // must match assets/zac-api.js
  var SENT_KEY    = 'sc_cart_sent';
  var DEBOUNCE_MS = 20000;          // collapse a burst of edits into one signal

  function chatId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)zc_chat_id=([^;]*)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  /* NOTE: we deliberately do NOT bail when there is no chat_id. A shopper who
     never took the fit test is exactly the one the Magic Checkout listener
     below exists to catch — they have no chat_id, but they do type a mobile
     number into the checkout panel. The cart watcher still costs them nothing:
     check() returns immediately without a chat_id. */

  var timer = null;
  var pending = false;      /* a debounced send is waiting */
  var lastCart = null;      /* cached so the unload flush has something to send */

  /* A cart permalink, so the WhatsApp message can drop them back into a filled
     cart instead of an empty one. Shopify restores /cart/<variant>:<qty>,...

     storefront=true matters: WITHOUT it Shopify 302s the shopper straight into
     checkout, skipping the cart page entirely, which is not what an "it is still
     in your cart" message should do. Verified 2 Sep 2026 — the bare permalink
     redirects to /checkouts/cn/..., and with the flag it lands on /cart and
     keeps working on repeat clicks (each visit mints a fresh cart_link_id),
     which a link sent over WhatsApp and opened hours later depends on. */
  function permalink(cart) {
    try {
      var parts = (cart.items || []).slice(0, 20).map(function (it) {
        return it.variant_id + ':' + it.quantity;
      });
      if (!parts.length) return '';
      return window.location.origin + '/cart/' + parts.join(',') + '?storefront=true';
    } catch (e) { return ''; }
  }

  /* ── Razorpay Magic Checkout capture ──────────────────────────────────────
     Clicking Checkout opens Magic Checkout as an iframe on THIS page, and its
     first step asks only for a mobile number. A shopper who types a number and
     leaves there is invisible to Shopify — no checkout record is created — so
     this is the only place that drop-off can be seen.

     Razorpay re-broadcasts its own events to the parent page (their bundle does
     `onmerchantevent: e => rzp.emit(e.event, e.data)`), so we can read them off
     window.message without touching their internals. We take only a phone
     number, hand it straight to our backend, and never store it.

     Set window.__SC_CART_SIGNAL_DEBUG = true before opening the panel to log
     event names and a masked number, for confirming which event carries it. */
  function digits(v) { return String(v == null ? '' : v).replace(/\D/g, ''); }

  /* Magic Checkout hands off to Shopify by navigating this page to
       /checkout?checkout[phone]=+91...&checkout[shipping_address][phone]=+91...
     so the number arrives inside a URL string rather than in a field called
     "phone". Scanning for the key name alone misses it entirely — verified
     against a real run on 2026-09-02. */
  function phoneFromText(s) {
    if (typeof s !== 'string' || s.indexOf('phone') === -1) return '';
    var dec; try { dec = decodeURIComponent(s); } catch (e) { dec = s; }
    var m = dec.match(/phone[\]\s]*=\s*(\+?\d[\d\s-]{7,17})/i);
    return m ? m[1].trim() : '';
  }

  function findPhone(obj, depth) {
    if (!obj || typeof obj !== 'object' || (depth || 0) > 6) return '';
    for (var k in obj) {
      var v = obj[k];
      if (v && typeof v === 'object') { var deep = findPhone(v, (depth || 0) + 1); if (deep) return deep; continue; }
      if (typeof v !== 'string' && typeof v !== 'number') continue;
      /* contact/phone/mobile fields only — never scrape arbitrary long digits,
         which would sweep up order ids, amounts and tokens. */
      if (!/contact|phone|mobile|msisdn/i.test(k)) continue;
      var d = digits(v);
      if (d.length >= 10 && d.length <= 15) return (String(v).charAt(0) === '+') ? ('+' + d) : d;
    }
    return '';
  }

  /* Normalise to E.164 and signal. Magic Checkout is India-first and its own
     field shows +91, but a number may arrive with or without the country code,
     and a shopper may type the trunk zero (09876...), which must not survive
     into an international number. */
  /* Same one phone rule as the backend (Code.gs normPhone_): a 10-digit
     Indian mobile -> E.164, or a foreign number that already carries its
     own +. Anything else is not sent — the backend would reject it anyway. */
  function normPhone(raw) {
    var s = String(raw || '').trim();
    var hasPlus = s.charAt(0) === '+';
    var d = digits(s);
    if (!d) return '';
    var nat;
    if (hasPlus) {
      if (d.slice(0, 2) === '91') {
        nat = d.slice(2);
        return /^[6-9]\d{9}$/.test(nat) ? '+91' + nat : '';
      }
      return (d.length >= 8 && d.length <= 15) ? '+' + d : '';
    }
    d = d.replace(/^0+/, '');
    if (d.length === 12 && d.slice(0, 2) === '91') nat = d.slice(2);
    else if (d.length === 10) nat = d;
    else return '';
    return /^[6-9]\d{9}$/.test(nat) ? '+91' + nat : '';
  }

  function capturePhone(raw) {
    var phone = normPhone(raw);
    if (!phone) return;
    sendWithIdentity({ phone: phone });
  }

  window.addEventListener('message', function (e) {
    if (!/\.razorpay\.com$/.test(String(e.origin).replace(/^https?:\/\//, ''))) return;
    var msg;
    try { msg = (typeof e.data === 'string') ? JSON.parse(e.data) : e.data; } catch (err) { return; }
    if (!msg || typeof msg !== 'object') return;

    /* Scan EVERY frame from Razorpay, not just `merchantevent`. They also emit
       `customevent` (magic.checkout_data) and `event` (shopify.order), and the
       contact details are not guaranteed to travel on the merchant channel.
       Two shapes to look for: a field actually named phone/contact/mobile, and
       a handoff URL with the number encoded into its query string. */
    var text = (typeof e.data === 'string') ? e.data : '';
    var phone = phoneFromText(text) || findPhone(msg, 0);
    if (window.__SC_CART_SIGNAL_DEBUG) {
      var name = (msg.data && (msg.data.event || msg.data.name)) || msg.event || 'frame';
      try { console.log('[cart-signal]', name, phone ? 'number ending ' + phone.slice(-3) : '(no number)'); } catch (err) {}
    }
    if (phone) capturePhone(phone);
  }, true);

  /* Belt and braces. If Magic Checkout builds that /checkout?... handoff URL and
     passes it to location.assign / location.replace, we see it here even when
     nothing came over window.message. A plain `location.href = url` cannot be
     intercepted, which is why the listener above stays the primary path. */
  (function guardNavigation() {
    ['assign', 'replace'].forEach(function (fn) {
      try {
        var orig = window.location[fn];
        if (typeof orig !== 'function') return;
        window.location[fn] = function (url) {
          try {
            var p = phoneFromText(String(url || ''));
            if (p) {
              if (window.__SC_CART_SIGNAL_DEBUG) {
                try { console.log('[cart-signal] location.' + fn, 'number ending ' + p.slice(-3)); } catch (err) {}
              }
              capturePhone(p);
            }
          } catch (err) {}
          return orig.apply(window.location, arguments);
        };
      } catch (err) { /* some browsers seal Location; the listener still covers us */ }
    });
  })();

  /* `identity` is {chat_id} for a fit-test shopper, or {phone} for one captured
     from the Magic Checkout panel. Everything else about the send is identical. */
  function post(identity, cart, urgent) {
    var items = (cart.items || []).map(function (it) {
      /* product_title, not title — `title` carries the variant suffix
         ("Model 1B - SH"), and Fire AI drop this straight into message copy. */
      return { title: it.product_title || it.title || '' };
    }).filter(function (x) { return x.title; });

    if (!items.length) return;

    /* Don't re-send the same cart on every page view. Session-scoped, so a
       genuine return visit tomorrow does signal again. Keyed on whichever
       identity we have, so a chat_id send and a phone send do not mask
       each other. */
    var who = identity.chat_id || identity.phone || '';
    var stamp = who + '|' + items.map(function (x) { return x.title; }).join('|');
    try { if (sessionStorage.getItem(SENT_KEY) === stamp) return; } catch (e) {}

    var payload = {
      action: 'cartSignal',
      pageToken: cfg.pageToken,
      items: items,
      cart_url: permalink(cart)
    };
    if (identity.chat_id) payload.chat_id = identity.chat_id;
    if (identity.phone) payload.phone = identity.phone;

    var body = JSON.stringify(payload);

    /* On unload a normal fetch is often killed before it leaves. sendBeacon is
       built for exactly this and survives the page going away, so it is the
       primary path when the tab is closing. text/plain keeps it a simple
       request, matching the endpoint's expectations. */
    if (urgent && navigator.sendBeacon) {
      try {
        var blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
        if (navigator.sendBeacon(cfg.endpoint, blob)) {
          try { sessionStorage.setItem(SENT_KEY, stamp); } catch (e) {}
          return;
        }
      } catch (e) { /* fall through to fetch */ }
    }

    fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },  // avoids a CORS preflight
      body: body,
      redirect: 'follow',
      keepalive: true
    }).then(function () {
      try { sessionStorage.setItem(SENT_KEY, stamp); } catch (e) {}
    }).catch(function () { /* a vendor outage must never break the cart */ });
  }

  function sendWithIdentity(identity) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (cart && cart.item_count > 0) { lastCart = cart; post(identity, cart); } })
      .catch(function () {});
  }

  function check() {
    pending = false;
    var id = chatId();
    if (!id) return;          /* no fit test taken; Magic Checkout path still applies */
    sendWithIdentity({ chat_id: id });
  }

  function schedule() {
    clearTimeout(timer);
    pending = true;
    /* Keep a fresh copy of the cart the moment it changes. On unload there is
       no time to fetch /cart.js, so the flush below needs it already in hand. */
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (cart) lastCart = cart; })
      .catch(function () {});
    timer = setTimeout(check, DEBOUNCE_MS);
  }

  /* The classic abandon is "add to cart, close the tab" — often inside the
     20-second debounce, which would otherwise mean we never signal at all.
     When the page goes away with a send still pending, fire it immediately
     using the cart we cached in schedule().

     pagehide covers closing and navigating away; visibilitychange covers the
     mobile case of switching apps, where pagehide is unreliable. Both funnel
     into one guarded flush, so a shopper who returns does not get a second
     signal — `pending` is cleared on the first one. */
  function flushNow() {
    if (!pending) return;
    pending = false;
    clearTimeout(timer);
    var id = chatId();
    if (!id || !lastCart || !lastCart.item_count) return;
    post({ chat_id: id }, lastCart, true);
  }

  window.addEventListener('pagehide', flushNow, true);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushNow();
  }, true);

  /* Watch for real cart mutations rather than guessing from the DOM, which
     differs per section and breaks whenever the theme is edited. Every theme
     path — quick add, PDP, cart page, drawer — ends up at one of these. */
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function (input) {
      var url = (typeof input === 'string') ? input : (input && input.url) || '';
      var p = _fetch.apply(this, arguments);
      if (CART_WRITE.test(url)) { try { p.then(function () { schedule(); }); } catch (e) {} }
      return p;
    };
  }

  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_WRITE.test(String(url || ''))) {
      this.addEventListener('load', function () { schedule(); });
    }
    return _open.apply(this, arguments);
  };

  /* Covers the shopper who added something yesterday and came back today
     without touching the cart — no mutation fires, but the cart is still live. */
  if (document.readyState === 'complete') check();
  else window.addEventListener('load', check);
})();
